import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const MIN_TEXT_LENGTH_FOR_DETECTION = 10
const MIN_DETECTION_CONFIDENCE = 0.5
const DEFAULT_SOURCE_LANGUAGE = 'en'

const getTargetLanguage = (): string =>
  (navigator.language || 'en').split('-')[0].toLowerCase()

const getTargetLanguageName = (languageCode: string): string => {
  try {
    const displayName = new Intl.DisplayNames(['en'], {
      type: 'language',
    }).of(languageCode)
    if (displayName) {
      return displayName
    }
  } catch {
    // Intl.DisplayNames not supported — fall through
  }
  return languageCode.toUpperCase()
}

const hasTranslatorApi = (): boolean =>
  typeof Translator !== 'undefined' && typeof LanguageDetector !== 'undefined'

const detectSourceLanguage = async (text: string): Promise<string> => {
  if (text.length < MIN_TEXT_LENGTH_FOR_DETECTION || !LanguageDetector) {
    return DEFAULT_SOURCE_LANGUAGE
  }
  const detectorAvailability = await LanguageDetector.availability()
  if (detectorAvailability === 'unavailable') {
    return DEFAULT_SOURCE_LANGUAGE
  }
  const detector = await LanguageDetector.create()
  const results = await detector.detect(text)
  detector.destroy?.()
  const [top] = results
  if (
    top &&
    top.confidence >= MIN_DETECTION_CONFIDENCE &&
    top.detectedLanguage
  ) {
    return top.detectedLanguage.toLowerCase()
  }
  return DEFAULT_SOURCE_LANGUAGE
}

type Status =
  | 'detecting'
  | 'idle'
  | 'translating'
  | 'translated'
  | 'hidden'
  | 'error'

interface UseReviewTranslationResult {
  shouldShowButton: boolean
  isTranslated: boolean
  isLoading: boolean
  isError: boolean
  targetLanguageName: string
  displayText: string
  onToggle: () => void
}

export const useReviewTranslation = (
  comment: string | undefined,
): UseReviewTranslationResult => {
  const isTranslatorSupported = useMemo(() => hasTranslatorApi(), [])
  const [status, setStatus] = useState<Status>(
    isTranslatorSupported ? 'detecting' : 'hidden',
  )
  const [translatedText, setTranslatedText] = useState<string | null>(null)
  const sourceLanguageRef = useRef(DEFAULT_SOURCE_LANGUAGE)
  const targetLanguage = useMemo(() => getTargetLanguage(), [])
  const targetLanguageName = useMemo(
    () => getTargetLanguageName(targetLanguage),
    [targetLanguage],
  )

  useEffect(() => {
    let cancelled = false

    if (comment && isTranslatorSupported) {
      const prepare = async (): Promise<void> => {
        try {
          sourceLanguageRef.current = await detectSourceLanguage(comment)

          if (cancelled) {
            return
          }

          if (sourceLanguageRef.current === targetLanguage) {
            setStatus('hidden')
            return
          }

          if (!Translator) {
            setStatus('hidden')
            return
          }

          const translatorAvailability = await Translator.availability({
            sourceLanguage: sourceLanguageRef.current,
            targetLanguage,
          })

          if (cancelled) {
            return
          }

          setStatus(
            translatorAvailability === 'unavailable' ? 'hidden' : 'idle',
          )
        } catch {
          if (!cancelled) {
            setStatus('hidden')
          }
        }
      }

      void prepare()
    }

    return () => {
      cancelled = true
    }
  }, [comment, isTranslatorSupported, targetLanguage])

  const runTranslation = useCallback(async (): Promise<void> => {
    if (!comment || !isTranslatorSupported) {
      return
    }

    setStatus('translating')

    try {
      if (!Translator) {
        setStatus('error')
        return
      }

      const translator = await Translator.create({
        sourceLanguage: sourceLanguageRef.current,
        targetLanguage,
      })
      const result = await translator.translate(comment)
      translator.destroy?.()

      setTranslatedText(result)
      setStatus('translated')
    } catch {
      setStatus('error')
    }
  }, [comment, isTranslatorSupported, targetLanguage])

  const onToggle = useCallback((): void => {
    if (status === 'translated') {
      setStatus('idle')
      return
    }
    if (status === 'idle' || status === 'error') {
      void runTranslation()
    }
  }, [status, runTranslation])

  const shouldShowButton =
    Boolean(comment) && status !== 'detecting' && status !== 'hidden'

  const isTranslated = status === 'translated' && translatedText !== null
  const isLoading = status === 'translating'
  const isError = status === 'error'

  const displayText =
    isTranslated && translatedText !== null ? translatedText : (comment ?? '')

  return {
    shouldShowButton,
    isTranslated,
    isLoading,
    isError,
    targetLanguageName,
    displayText,
    onToggle,
  }
}
